import { useState, useEffect } from 'react';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '@/hooks/use-notifications';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {Bell, Loader2, Save} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PreferenceRow {
  type: string;
  name: string;
  description: string;
  email: boolean;
  sms: boolean;
  inApp: boolean;
  push: boolean;
}

const notificationTypeInfo: Record<string, { name: string; description: string }> = {
  booking_request_submitted: {
    name: 'Booking Request Submitted',
    description: 'When you submit a new booking request',
  },
  booking_approved: {
    name: 'Booking Approved',
    description: 'When your booking is approved',
  },
  booking_rejected: {
    name: 'Booking Rejected',
    description: 'When your booking is rejected',
  },
  booking_rescheduled: {
    name: 'Booking Rescheduled',
    description: 'When your booking is rescheduled',
  },
  booking_cancelled: {
    name: 'Booking Cancelled',
    description: 'When your booking is cancelled',
  },
  video_uploaded: {
    name: 'Video Uploaded',
    description: 'When your video is uploaded and ready for review',
  },
  video_published: {
    name: 'Video Published',
    description: 'When your video is made public',
  },
  password_reset: {
    name: 'Password Reset',
    description: 'When you request a password reset',
  },
};

export function NotificationPreferences() {
  const { toast } = useToast();
  const { data: preferences, isLoading } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();
  
  const [localPreferences, setLocalPreferences] = useState<PreferenceRow[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (preferences) {
      // Filter out admin notification types
      const rows = preferences
        .filter(pref => !pref.notificationType.startsWith('admin_'))
        .map(pref => ({
          type: pref.notificationType,
          name: notificationTypeInfo[pref.notificationType]?.name || pref.notificationType,
          description: notificationTypeInfo[pref.notificationType]?.description || '',
          email: pref.email,
          sms: pref.sms,
          inApp: pref.inApp,
          push: pref.push,
        }));
      setLocalPreferences(rows);
    }
  }, [preferences]);

  const handleToggle = (type: string, channel: 'email' | 'sms' | 'inApp' | 'push') => {
    setLocalPreferences(prev => 
      prev.map(pref => 
        pref.type === type 
          ? { ...pref, [channel]: !pref[channel] }
          : pref
      )
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Get all admin preferences to preserve them
    const adminPreferences = preferences?.filter(
      pref => pref.notificationType.startsWith('admin_')
    ) || [];

    // Combine regular preferences with preserved admin preferences
    const updatedPreferences = [
      ...localPreferences.map(pref => ({
        notificationType: pref.type,
        email: pref.email,
        sms: pref.sms,
        inApp: pref.inApp,
        push: pref.push,
      })),
      ...adminPreferences
    ];

    try {
      await updatePreferences.mutateAsync(updatedPreferences);
      setHasChanges(false);
      toast({
        title: 'Preferences saved',
        description: 'Your notification preferences have been updated.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save preferences. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <Card className="border-white/10">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-white/70" />
          <CardTitle>Notification Preferences</CardTitle>
        </div>
        <CardDescription>
          Choose how you want to receive notifications for different events
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 pr-4 text-muted-foreground">Notification Type</th>
                  <th className="text-center px-4 py-2 min-w-[80px] text-muted-foreground">Email</th>
                  <th className="text-center px-4 py-2 min-w-[80px] text-muted-foreground">In-App</th>
                </tr>
              </thead>
              <tbody>
                {localPreferences.map((pref) => (
                  <tr key={pref.type} className="border-b border-white/10">
                    <td className="py-4 pr-4">
                      <div>
                        <p className="font-medium text-foreground">{pref.name}</p>
                        <p className="text-sm text-muted-foreground">{pref.description}</p>
                      </div>
                    </td>
                    <td className="text-center px-4 py-4">
                      <Switch
                        checked={pref.email}
                        onCheckedChange={() => handleToggle(pref.type, 'email')}
                      />
                    </td>
                    <td className="text-center px-4 py-4">
                      <Switch
                        checked={pref.inApp}
                        onCheckedChange={() => handleToggle(pref.type, 'inApp')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {hasChanges && (
            <div className="flex justify-end pt-4 border-t border-white/10">
              <Button 
                onClick={handleSave}
                disabled={updatePreferences.isPending}
                className="mt-4"
              >
                {updatePreferences.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Preferences
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}